import { ProjectFlowStepStatus, ProjectFlowPhaseKey, ProjectFlow } from '@/types';
import { formatDate } from '@/utils/date_utils';

export const MAX_PHOTOS = 10;
export const MAX_PHOTO_BYTES = 15 * 1024 * 1024; // 15 MB
export const MAX_TOTAL_BYTES = 40 * 1024 * 1024; // 40 MB

export function phaseStatusColor(status: ProjectFlowStepStatus): 'success' | 'primary' | 'default' {
    switch (status) {
        case 'done':
            return 'success';
        case 'in_progress':
            return 'primary';
        case 'pending':
            return 'default';
    }
}

export function phaseStatusBorderColor(status: ProjectFlowStepStatus): string {
    switch (status) {
        case 'done':
            return 'success.main';
        case 'in_progress':
            return 'primary.main';
        case 'pending':
            return 'grey.400';
    }
}

export function stepStatusLabel(status: ProjectFlowStepStatus): string {
    switch (status) {
        case 'pending':
            return 'Pending';
        case 'in_progress':
            return 'In Progress';
        case 'done':
            return 'Done';
    }
}

export function nextStatus(status: ProjectFlowStepStatus): ProjectFlowStepStatus {
    switch (status) {
        case 'pending':
            return 'in_progress';
        case 'in_progress':
            return 'done';
        case 'done':
            return 'pending';
    }
}

export function formatFlowDate(iso: string | null | undefined): string {
    if (!iso) return '';
    return formatDate(iso, 'MMM d, yyyy HH:mm');
}

export function isCommentablePhase(key: ProjectFlowPhaseKey): boolean {
    return key === 'fields' || key === 'warranty' || key === 'maintenance';
}

export function findPhase(flow: ProjectFlow, key: ProjectFlowPhaseKey) {
    return flow.phases.find(p => p.key === key);
}

export function validateAttachmentSelection(files: File[]): {
    valid: boolean;
    error?: string;
} {
    if (files.length > MAX_PHOTOS) {
        return {
            valid: false,
            error: `Maximum ${MAX_PHOTOS} files allowed per comment.`,
        };
    }

    let totalBytes = 0;
    for (const file of files) {
        if (file.size > MAX_PHOTO_BYTES) {
            return {
                valid: false,
                error: 'Each file must be 15 MB or smaller.',
            };
        }
        totalBytes += file.size;
    }

    if (totalBytes > MAX_TOTAL_BYTES) {
        return {
            valid: false,
            error: 'Files exceed 40 MB total; attach fewer per comment.',
        };
    }

    return { valid: true };
}

export function validatePhotoSelection(files: File[]): {
    valid: boolean;
    error?: string;
} {
    if (files.length > MAX_PHOTOS) {
        return {
            valid: false,
            error: `Maximum ${MAX_PHOTOS} photos allowed per comment.`,
        };
    }

    let totalBytes = 0;
    for (const file of files) {
        if (file.size > MAX_PHOTO_BYTES) {
            return {
                valid: false,
                error: 'Each photo must be 15 MB or smaller.',
            };
        }
        totalBytes += file.size;
    }

    if (totalBytes > MAX_TOTAL_BYTES) {
        return {
            valid: false,
            error: 'Photos exceed 40 MB total; post fewer per comment.',
        };
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    for (const file of files) {
        // Normalize image/jpg to image/jpeg
        let type = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
        if (!allowedTypes.includes(type)) {
            if (type === 'image/heic' || type === 'image/heif') {
                return {
                    valid: false,
                    error: 'HEIC is not supported; please upload JPG, PNG or WebP.',
                };
            }
            return {
                valid: false,
                error: 'Only JPG, PNG, and WebP images are supported.',
            };
        }
    }

    return { valid: true };
}
